/**
 * 自前収集への送信（POST {apiBase}/events）。
 *
 * `navigator.sendBeacon` を使うのは、ページを離れる瞬間でも確実に出るため。Blob の型を
 * `text/plain;charset=UTF-8` にしてあるのは CORS のセーフリスト内に収めるためで、これで
 * **プリフライトが起きない**（unload 中に preflight は間に合わない）。サーバ側は
 * Content-Type を見ずに本文を JSON として読む。
 *
 * 送信の失敗はすべて握り潰す。分析が届かないことより、読書が止まることの方が重い。
 */
import type { AnalyticsAdapter, AnalyticsContext, AnalyticsEvent } from '../types';

export class BeaconAdapter implements AnalyticsAdapter {
  constructor(private readonly view: Window) {}

  send(events: readonly AnalyticsEvent[], context: AnalyticsContext): void {
    if (context.apiBase === null || events.length === 0) return;

    const body = JSON.stringify(this.envelope(events, context));
    const url = context.apiBase + '/events';
    try {
      const beacon = this.view.navigator?.sendBeacon;
      if (beacon !== undefined) {
        const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
        if (beacon.call(this.view.navigator, url, blob)) return;
      }
      // sendBeacon が無い / キューが一杯 / サイズ超過。keepalive でもう一度だけ試す。
      void this.view
        .fetch?.(url, { method: 'POST', body, keepalive: true, credentials: 'same-origin' })
        .catch(() => {});
    } catch {
      /* 計測の失敗は閲覧に影響させない */
    }
  }

  /**
   * 封筒。book と sid はバッチ単位で 1 回だけ載せる（1 ビューワ＝1 ブック・1 セッションなので、
   * イベントごとに繰り返す意味がない）。
   *
   * 流入元 ref は**そのセッションの最初のバッチにだけ**付ける（以後は同じ値で使い道がない）。
   */
  private envelope(
    events: readonly AnalyticsEvent[],
    context: AnalyticsContext,
  ): Record<string, unknown> {
    const envelope: Record<string, unknown> = {
      book: context.book,
      sid: events[0]?.sid ?? '',
      events: events.map((event) => ({
        event: event.event,
        ts: event.ts,
        ...(event.page === undefined ? {} : { page: event.page }),
        ...(event.value === undefined ? {} : { value: event.value }),
      })),
    };
    if (context.firstBatch && context.referrer !== null && context.referrer !== '') {
      envelope.ref = context.referrer;
    }
    return envelope;
  }
}
